import React, { useState } from 'react';
import { useAllGuarantorRequests, useAcceptGuarantor, useRejectGuarantor, useUpdateGuarantorAmount } from '../hooks/useLoanGuarantors.ts';
import { CheckCircle, XCircle, Clock, AlertCircle, Users, Edit3, Save, X } from 'lucide-react';
import { PERMISSIONS } from '../utils/permissions';

const GuarantorRequestCard = ({ request, onAccept, onReject, onUpdateAmount, isProcessing }) => {
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState(request.guaranteed_amount || 0);
  const [isUpdatingAmount, setIsUpdatingAmount] = useState(false);
  const getStatusIcon = () => {
    switch (request.status) {
      case 'accepted':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-yellow-500" size={20} />;
    }
  };

  const getStatusText = () => {
    switch (request.status) {
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = () => {
    switch (request.status) {
      case 'accepted':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const handleSaveAmount = async () => {
    if (editedAmount < 0) {
      alert('Amount cannot be negative');
      return;
    }
    
    setIsUpdatingAmount(true);
    try {
      await onUpdateAmount(request.loan_id, request.id, editedAmount);
      setIsEditingAmount(false);
    } catch (error) {
      console.error('Failed to update amount:', error);
    } finally {
      setIsUpdatingAmount(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedAmount(request.guaranteed_amount || 0);
    setIsEditingAmount(false);
  };

  return (
    <div className="bg-white border rounded-lg p-6 mb-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Loan Request from {request.loan?.member?.name}
          </h3>
          <p className="text-sm text-gray-600">
            {request.loan?.loanType?.name} - {request.loan?.principal?.toLocaleString()}
          </p>
        </div>
        <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor()}`}>
          {getStatusIcon()}
          <span className="ml-1">{getStatusText()}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <h4 className="font-medium text-gray-700 mb-1">Loan Details</h4>
          <p className="text-sm text-gray-600">
            Amount: {request.loan?.principal?.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600">
            Type: {request.loan?.loanType?.name}
          </p>
          {request.member_savings !== undefined && (
            <p className="text-sm text-gray-600">
              Member Savings: {request.member_savings.toLocaleString()}
            </p>
          )}
        </div>
        <div>
          <h4 className="font-medium text-gray-700 mb-1">Your Guarantee</h4>
          {request.status === 'pending' ? (
            <div className="space-y-2">
              {request.calculated_guarantor_amount !== undefined && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Amount Needed:</span> {request.calculated_guarantor_amount.toLocaleString()}
                </div>
              )}
              {isEditingAmount ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={editedAmount}
                    onChange={(e) => setEditedAmount(parseFloat(e.target.value))}
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    min="0"
                    step="0.01"
                  />
                  <button
                    onClick={handleSaveAmount}
                    disabled={isUpdatingAmount}
                    className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                  >
                    <Save size={16} />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isUpdatingAmount}
                    className="p-1 text-gray-600 hover:text-gray-700 disabled:opacity-50"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-blue-600">
                    {request.guaranteed_amount?.toLocaleString() || '0.00'}
                  </span>
                  <button
                    onClick={() => setIsEditingAmount(true)}
                    className="p-1 text-gray-600 hover:text-gray-700"
                  >
                    <Edit3 size={16} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-lg font-semibold text-blue-600">
              {request.guaranteed_amount?.toLocaleString() || '0.00'}
            </p>
          )}
        </div>
      </div>

      {request.status !== 'pending' && (
        <div className="text-sm text-gray-500">
          <p>Requested on: {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}</p>
          {request.confirmed_at && (
            <p>Processed on: {new Date(request.confirmed_at).toLocaleDateString()} at {new Date(request.confirmed_at).toLocaleTimeString()}</p>
          )}
        </div>
      )}

      {request.status === 'pending' && (
        <div className="text-sm text-gray-500">
          <p>Requested on: {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}</p>
        </div>
      )}

      {request.status === 'pending' && (
        <div className="flex gap-3">
          <button
            onClick={() => onAccept(request.loan_id, request.id, editedAmount || request.guaranteed_amount)}
            disabled={isProcessing}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <CheckCircle className="mr-2" size={16} />
            Accept
          </button>
          <button
            onClick={() => onReject(request.loan_id, request.id)}
            disabled={isProcessing}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <XCircle className="mr-2" size={16} />
            Reject
          </button>
        </div>
      )}

      
    </div>
  );
};

export default function GuarantorRequestsPage() {
  const { data: requests, isLoading, error } = useAllGuarantorRequests();
  const acceptGuarantor = useAcceptGuarantor();
  const rejectGuarantor = useRejectGuarantor();
  const updateGuarantorAmount = useUpdateGuarantorAmount();

  const handleAccept = (loanId, guarantorId, amount) => {
    acceptGuarantor.mutate({ 
      loanId, 
      guarantorId, 
      guaranteed_amount: amount 
    });
  };

  const handleReject = (loanId, guarantorId) => {
    rejectGuarantor.mutate({ loanId, guarantorId });
  };

  const handleUpdateAmount = async (loanId, guarantorId, amount) => {
    return updateGuarantorAmount.mutateAsync({ loanId, guarantorId, guaranteed_amount: amount });
  };

  const isProcessing = acceptGuarantor.isPending || rejectGuarantor.isPending || updateGuarantorAmount.isPending;

  // Filter requests by status
  const pendingRequests = requests?.filter(req => req.status === 'pending') || [];
  const acceptedRequests = requests?.filter(req => req.status === 'accepted') || [];
  const rejectedRequests = requests?.filter(req => req.status === 'rejected') || [];
  const allRequests = requests || [];

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-2" />
            <span className="text-red-800">Error loading guarantor requests</span>
          </div>
        </div>
      </div>
    );
  }



  return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center mb-6">
          <Users className="text-blue-600 mr-3" size={24} />
          <h1 className="text-2xl font-bold">Guarantor Requests</h1>
        </div>

      {/* All Requests Summary */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-lg p-4 border bg-[var(--card-bg)]">
            <div className="text-2xl font-bold text-primary">{allRequests.length}</div>
            <div className="text-sm text-muted">Total Requests</div>
          </div>
          <div className="rounded-lg p-4 border bg-[var(--card-bg)]">
            <div className="text-2xl font-bold text-warning"> {pendingRequests.length}</div>
            <div className="text-sm text-muted">Pending</div>
          </div>
          <div className="rounded-lg p-4 border bg-[var(--card-bg)]">
            <div className="text-2xl font-bold text-success"> {acceptedRequests.length}</div>
            <div className="text-sm text-muted">Accepted</div>
          </div>
          <div className="rounded-lg p-4 border bg-[var(--card-bg)]">
            <div className="text-2xl font-bold text-danger"> {rejectedRequests.length}</div>
            <div className="text-sm text-muted">Rejected</div>
          </div>
        </div>
      </div>


      {/* No Requests Message */}
      {allRequests.length === 0 && (
        <div className="bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg text-center">
          <Users className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No Guarantor Requests</h3>
          <p className="text-gray-500">
            You haven't received any guarantor requests yet.
          </p>
        </div>
      )}

      {/* Pending Requests */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">
          Pending Requests ({pendingRequests.length})
        </h2>
        {pendingRequests.length > 0 ? (
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <GuarantorRequestCard
                key={request.id}
                request={request}
                onAccept={handleAccept}
                onReject={handleReject}
                onUpdateAmount={handleUpdateAmount}
                isProcessing={isProcessing}
              />
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg text-center">
            <CheckCircle className="text-green-400 mx-auto mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Pending Requests</h3>
            <p className="text-gray-500">
              You don't have any pending guarantor requests at the moment.
            </p>
          </div>
        )}
      </div>

      {/* Accepted Requests */}
      {acceptedRequests.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">
            Accepted Requests ({acceptedRequests.length})
          </h2>
          <div className="space-y-4">
            {acceptedRequests.map((request) => (
              <GuarantorRequestCard
                key={request.id}
                request={request}
                onAccept={handleAccept}
                onReject={handleReject}
                onUpdateAmount={handleUpdateAmount}
                isProcessing={isProcessing}
              />
            ))}
          </div>
        </div>
      )}

      {/* Rejected Requests */}
      {rejectedRequests.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">
            Rejected Requests ({rejectedRequests.length})
          </h2>
          <div className="space-y-4">
            {rejectedRequests.map((request) => (
              <GuarantorRequestCard
                key={request.id}
                request={request}
                onAccept={handleAccept}
                onReject={handleReject}
                onUpdateAmount={handleUpdateAmount}
                isProcessing={isProcessing}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 